import { useState, createContext, useContext, useEffect } from "react";

// Theme definitions
const themes = {
  dark: {
    bg:"#07090d", panel:"#0b1018", panel2:"#0e1520",
    border:"#131d2b", border2:"#1a2638",
    text:"#cdd8e8", dim:"#4d6580", dim2:"#5d7898",
    green:"#00d48a", blue:"#3a9eff", amber:"#f0a020",
    red:"#ff4040", purple:"#9b72ff", cyan:"#00c8d4",
    barBg:"#0c1520",
  },
  light: {
    bg:"#eef1f5", panel:"#ffffff", panel2:"#f5f6f9",
    border:"#d0d5de", border2:"#b8c0d0",
    text:"#1a1e2e", dim:"#7080a0", dim2:"#586880",
    green:"#00915e", blue:"#1870d0", amber:"#c07800",
    red:"#c02020", purple:"#6040b8", cyan:"#007888",
    barBg:"#dce0e8",
  },
};

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => localStorage.getItem("gpu_fabric_theme") || "dark");

  useEffect(() => {
    localStorage.setItem("gpu_fabric_theme", mode);
    _setCurrentTheme(themes[mode]);
  }, [mode]);

  _setCurrentTheme(themes[mode]);

  const toggle = () => setMode(m => m === "dark" ? "light" : "dark");

  return (
    <ThemeContext.Provider value={{ mode, toggle, C: themes[mode] }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

// C is a reactive proxy — reads from _currentTheme which ThemeProvider updates
let _currentTheme = themes.dark;
export function _setCurrentTheme(t) { _currentTheme = t; }
export const C = new Proxy({}, { get: (_, key) => _currentTheme[key] });

export const SELF_SERVE = { max_cpu_delta:4, max_ram_delta:16 };

export const uc = (u) => {
  const USER_COLORS = { admin:"#ff4040", default:"#7ecbff" };
  return USER_COLORS[u] || [
    "#00d48a","#3a9eff","#9b72ff","#f0a020","#00c8d4","#ff6b9d","#ffd93d","#6bcb77"
  ][(u||"").split("").reduce((a,c)=>a+c.charCodeAt(0),0) % 8];
};

export const Bar = ({ pct, color, h=3, C:tc }) => {
  const { C: thC } = useTheme();
  const c = tc || thC;
  return (
    <div style={{ height:h, background:c.barBg, borderRadius:99, overflow:"hidden" }}>
      <div style={{ width:`${Math.min(pct,100)}%`, height:"100%", background:color,
        borderRadius:99, transition:"width 1s ease",
        boxShadow:pct>0?`0 0 5px ${color}44`:"none" }} />
    </div>
  );
};

export const Dot = ({ color, pulse }) => (
  <span style={{ display:"inline-block", width:6, height:6, borderRadius:"50%",
    background:color, boxShadow:`0 0 5px ${color}`, flexShrink:0,
    animation:pulse?"blink 2s infinite":"none" }} />
);

export const Badge = ({ label, color }) => {
  const { C: c } = useTheme();
  const cl = color || c.dim2;
  return (
    <span style={{ fontSize:10, padding:"2px 8px", borderRadius:20,
      background:cl+"18", color:cl, border:`1px solid ${cl}30`,
      display:"inline-flex", alignItems:"center", gap:4,
      fontFamily:"monospace", whiteSpace:"nowrap" }}>
      <Dot color={cl}/>{label}
    </span>
  );
};

export const sC_fn = (c) => ({ running:c.green, stopped:c.dim2, pending:c.amber,
  reclaimed:c.red, restarting:c.blue, exited:c.red, created:c.dim2,
  cloning:c.purple, error:c.red });
export const sC = sC_fn(themes.dark);
export const statusBadge = s => <Badge label={s} color={sC[s]}/>;

export const Btn = ({ children, onClick, variant="default", small, disabled, full }) => {
  const { C: c } = useTheme();
  const V = {
    default:{ bg:c.border2, cc:c.text, bd:c.border2 },
    green:{ bg:c.green+"18", cc:c.green, bd:c.green+"44" },
    red:{ bg:c.red+"18", cc:c.red, bd:c.red+"44" },
    amber:{ bg:c.amber+"18", cc:c.amber, bd:c.amber+"44" },
    blue:{ bg:c.blue+"18", cc:c.blue, bd:c.blue+"44" },
    ghost:{ bg:"transparent", cc:c.dim2, bd:c.border },
    cyan:{ bg:c.cyan+"18", cc:c.cyan, bd:c.cyan+"44" },
    purple:{ bg:c.purple+"18", cc:c.purple, bd:c.purple+"44" },
  }[variant] ?? { bg:c.border2, cc:c.text, bd:c.border2 };
  return (
    <button onClick={disabled?undefined:onClick} style={{
      background:V.bg, color:V.cc, border:`1px solid ${V.bd}`,
      borderRadius:6, padding:small?"3px 10px":"6px 14px",
      fontSize:small?10:12, fontFamily:"monospace", fontWeight:700,
      cursor:disabled?"not-allowed":"pointer", opacity:disabled?.4:1,
      width:full?"100%":"auto", whiteSpace:"nowrap", lineHeight:1.4,
    }}>{children}</button>
  );
};

export const Card = ({ children, style, onClick }) => {
  const { C: c } = useTheme();
  return (
    <div onClick={onClick} style={{ background:c.panel, border:`1px solid ${c.border}`,
      borderRadius:10, overflow:"hidden", ...style }}>{children}</div>
  );
};

export const CardHead = ({ children }) => {
  const { C: c } = useTheme();
  return (
    <div style={{ padding:"11px 16px", borderBottom:`1px solid ${c.border}`,
      background:c.panel2, display:"flex", justifyContent:"space-between",
      alignItems:"center" }}>{children}</div>
  );
};

export const SectionTitle = ({ title, action }) => {
  const { C: c } = useTheme();
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
      marginBottom:14, paddingBottom:10, borderBottom:`1px solid ${c.border}` }}>
      <span style={{ fontSize:9, color:c.dim2, fontFamily:"monospace",
        letterSpacing:2 }}>{title}</span>
      {action}
    </div>
  );
};

export const Modal = ({ title, onClose, children, width=500 }) => {
  const { C: c } = useTheme();
  return (
    <div style={{ position:"fixed", inset:0, background:"#000b", zIndex:200,
      display:"flex", alignItems:"center", justifyContent:"center" }}
      onClick={onClose}>
      <div style={{ background:c.panel, border:`1px solid ${c.border2}`,
        borderRadius:12, padding:26, width, maxWidth:"96vw",
        boxShadow:"0 32px 80px #0009", maxHeight:"90vh", overflowY:"auto" }}
        onClick={e=>e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between",
          alignItems:"center", marginBottom:20 }}>
          <span style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:16,
            fontWeight:700, color:c.text, letterSpacing:1 }}>{title}</span>
          <button onClick={onClose} style={{ background:"none", border:"none",
            color:c.dim2, cursor:"pointer", fontSize:20, lineHeight:1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
};

export const Field = ({ label, value, onChange, type="text", options, placeholder, readOnly }) => {
  const { C: c } = useTheme();
  return (
    <div style={{ marginBottom:13 }}>
      <div style={{ fontSize:9, color:c.dim2, fontFamily:"monospace",
        letterSpacing:1, marginBottom:5, textTransform:"uppercase" }}>{label}</div>
      {options ? (
        <select value={value} onChange={e=>onChange(e.target.value)} style={{
          width:"100%", background:c.panel2, border:`1px solid ${c.border2}`,
          borderRadius:6, color:c.text, padding:"8px 10px", fontSize:12,
          fontFamily:"monospace", outline:"none" }}>
          {options.map(o=><option key={o.v??o} value={o.v??o}>{o.l??o}</option>)}
        </select>
      ) : (
        <input readOnly={readOnly} type={type} value={value}
          placeholder={placeholder} onChange={e=>onChange?.(e.target.value)}
          style={{ width:"100%",
            background:readOnly?c.border:c.panel2,
            border:`1px solid ${readOnly?c.border:c.border2}`,
            borderRadius:6, color:readOnly?c.dim2:c.text,
            padding:"8px 10px", fontSize:12, fontFamily:"monospace",
            outline:"none", boxSizing:"border-box" }} />
      )}
    </div>
  );
};

export const GpuMini = ({ gpu }) => {
  const { C: c } = useTheme();
  const free = !gpu.assigned;
  const uC = gpu.util>=80?c.red:gpu.util>=50?c.amber:gpu.util>0?c.green:c.dim2;
  const user = gpu.assigned;
  return (
    <div style={{ background:c.bg,
      border:`1px solid ${free?c.border:uc(user)+"44"}`,
      borderRadius:8, padding:"10px 12px", position:"relative", overflow:"hidden" }}>
      {!free && <div style={{ position:"absolute",top:0,left:0,right:0,height:2,background:uc(user) }}/>}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:7 }}>
        <span style={{ fontSize:9, color:c.dim2 }}>GPU {gpu.id}</span>
        {free
          ? <span style={{ fontSize:9, color:c.dim2 }}>여유</span>
          : <span style={{ fontSize:9, color:uc(user), background:uc(user)+"18",
              padding:"2px 7px", borderRadius:20 }}>{user}</span>}
      </div>
      <div style={{ marginBottom:4 }}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:2 }}>
          <span style={{ fontSize:8, color:c.dim2 }}>UTIL</span>
          <span style={{ fontSize:10, color:uC, fontWeight:700 }}>{gpu.util}%</span>
        </div>
        <Bar pct={gpu.util} color={uC} h={3}/>
      </div>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
        <span style={{ fontSize:8, color:c.dim2 }}>VRAM {(gpu.mem_used/1024).toFixed(1)}/{(gpu.mem_total/1024).toFixed(0)}G</span>
      </div>
      <Bar pct={(gpu.mem_used/gpu.mem_total)*100} color={c.blue} h={2}/>
    </div>
  );
};

export const SshCopyBlock = ({ label, text }) => {
  const { C: c } = useTheme();
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ background:c.bg, border:`1px solid ${c.border2}`,
      borderRadius:6, padding:"8px 12px", display:"flex",
      justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
      <div>
        {label && <div style={{ fontSize:8, color:c.dim2, marginBottom:3 }}>{label}</div>}
        <span style={{ fontSize:11, color:c.cyan, fontFamily:"monospace" }}>{text}</span>
      </div>
      <button onClick={()=>{navigator.clipboard?.writeText(text);setCopied(true);setTimeout(()=>setCopied(false),2000);}}
        style={{ background:"none", border:"none", color:copied?c.green:c.dim2,
          cursor:"pointer", fontSize:10, fontFamily:"monospace" }}>
        {copied ? "✓" : "copy"}
      </button>
    </div>
  );
};
